import React, { useEffect, useState } from "react";
import { Box, Divider, Link, Pagination, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventNoteIcon from "@mui/icons-material/EventNote";
import RepeatIcon from "@mui/icons-material/Repeat";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { useQuery } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { Link as RouterLink } from "react-router-dom";
import { getUpcomingRecurringSchedules } from "../../../fetchers/cmmsRecurringSchedule";
import { getAssetActivePreventiveWorkOrders } from "../../../fetchers/cmmsWorkOrder";
import recurringScheduleMessages from "../../cmmsRecurringSchedules/messages";
import { ScheduleTriggerBasis } from "../../cmmsRecurringSchedules/scheduleEnums";
import { renderBadge } from "../../cmmsWorkOrders/components/WorkOrdersDataGrid";
import { buildWorkOrderDeepLink } from "../../cmmsWorkOrders/workOrderDeepLink";
import { getStatusBadge } from "../../cmmsWorkOrders/workOrderEnums";
import { getTriggerBasisLabel } from "../cmmsMessages";
import { formatTimestamp } from "../cmmsFormat";
import { buildScheduleReadingLabel } from "../scheduleReset";
import { getAssetHierarchyPalette } from "../assetHierarchyStyles";
import { CmmsEmptyState, CmmsErrorState, CmmsListSkeleton } from "./CmmsStates";
import ScheduleProgress from "./ScheduleProgress";
import messages from "../messages";

/*
 * The maintenance still ahead of a node: its preventive work orders that are not done yet, and
 * the schedules that will raise the next ones. Both cover the node and everything below it, like
 * the history tab next door — at a site that is every machine on it.
 *
 * Two lists rather than one because they answer different questions. An order is work somebody
 * has been given; a schedule is work nobody has been given yet. Merging them would put a date
 * someone planned next to a date a frequency produced and invite reading them as the same thing.
 *
 * The orders are paged on the server, the schedules are not. Done orders fall into the history
 * tab, but nothing makes them get done: the calendar sweep raises the next order on its date
 * whether or not the last one is still open, so a site that has fallen behind carries any number
 * of them. A node carries a handful of schedules, one per thing somebody set up. The server sorts
 * both, overdue first.
 *
 * Only mounted while its tab is selected, so neither request is made until someone looks, and
 * keyed by node in AssetNodeDetailsPanel, so paging starts over on a new node.
 */
const WORK_ORDER_PAGE_SIZE = 10;

// The same three icons the schedules grid uses for its basis column.
const TRIGGER_ICONS = {
  [ScheduleTriggerBasis.Calendar]: CalendarMonthIcon,
  [ScheduleTriggerBasis.RunningTime]: ScheduleIcon,
  [ScheduleTriggerBasis.Quantity]: RepeatIcon,
};

const getCaptionSx = (cmmsColors) => ({
  color: cmmsColors.mutedText,
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

function WorkOrderRow({ workOrder, cmmsColors }) {
  const intl = useIntl();

  // At a site most orders belong to a machine below it, so which one leads the second line.
  const caption = [
    workOrder.assetNodeName,
    workOrder.plannedStartDate &&
      intl.formatMessage(messages.maintenancePlannedStart, { date: formatTimestamp(intl, workOrder.plannedStartDate) }),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.75 }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {/* A real link, as in the history tab, so ctrl-click opens the order in a new tab. */}
        <Link
          component={RouterLink}
          to={buildWorkOrderDeepLink(workOrder.id)}
          underline="hover"
          variant="body2"
          sx={{ ...getCaptionSx(cmmsColors), color: cmmsColors.accent, fontWeight: 700 }}
        >
          {workOrder.title}
        </Link>
        {caption && (
          <Typography variant="caption" sx={getCaptionSx(cmmsColors)}>
            {caption}
          </Typography>
        )}
      </Box>
      <Box sx={{ flexShrink: 0 }}>{renderBadge(getStatusBadge(workOrder.status, intl))}</Box>
    </Box>
  );
}

function UpcomingScheduleRow({ schedule, cmmsColors }) {
  const intl = useIntl();
  const isCalendar = schedule.triggerBasis === ScheduleTriggerBasis.Calendar;
  const TriggerIcon = TRIGGER_ICONS[schedule.triggerBasis] ?? CalendarMonthIcon;
  const caption = [schedule.assetNodeName, schedule.toolName].filter(Boolean).join(" · ");

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.75 }}>
      <Tooltip title={getTriggerBasisLabel(schedule.triggerBasis, intl)} arrow>
        <Box sx={{ color: cmmsColors.subtleText, display: "flex", alignItems: "center", flexShrink: 0 }}>
          <TriggerIcon fontSize="small" />
        </Box>
      </Tooltip>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ ...getCaptionSx(cmmsColors), color: cmmsColors.text, fontWeight: 700 }}>
          {schedule.title}
        </Typography>
        {caption && (
          <Typography variant="caption" sx={getCaptionSx(cmmsColors)}>
            {caption}
          </Typography>
        )}
      </Box>

      {isCalendar ? (
        <Tooltip title={intl.formatMessage(recurringScheduleMessages.columnNextRun)} arrow>
          <Typography
            variant="body2"
            sx={{ color: schedule.isOverdue ? cmmsColors.dangerText : cmmsColors.text, fontWeight: 700, flexShrink: 0, textAlign: "right" }}
          >
            {formatTimestamp(intl, schedule.nextRunDate)}
            {schedule.isOverdue && (
              <Typography component="span" variant="body2" sx={{ color: cmmsColors.dangerText, fontWeight: 700, ml: 0.5 }}>
                {intl.formatMessage(messages.usageScheduleOverdue)}
              </Typography>
            )}
          </Typography>
        </Tooltip>
      ) : (
        // The row is already the reading the bar wants, and the tooltip spells out the figures
        // behind the percentage. A schedule nothing could measure — a tool with no threshold, a
        // node with no machine — says so rather than showing a count of nothing.
        <Tooltip
          title={
            schedule.percentConsumed != null
              ? buildScheduleReadingLabel(schedule, intl)
              : intl.formatMessage(messages.maintenanceNotMeasured)
          }
          arrow
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
            <ScheduleProgress reading={schedule} warnAtPercent={schedule.warnAtPercent} cmmsColors={cmmsColors} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}

/*
 * `rows` is what to show now, which for a paged list is one page of `count`. `footer` goes under
 * the rows, and `isPaging` dims them while the next page loads in their place.
 */
function MaintenanceSection({
  title,
  count,
  query,
  rows,
  isPaging = false,
  footer,
  loadFailed,
  emptyIcon,
  emptyTitle,
  emptyHint,
  getRowKey,
  renderRow,
  cmmsColors,
}) {
  const intl = useIntl();

  let body;
  if (query.isLoading) {
    body = <CmmsListSkeleton rows={3} />;
  } else if (query.isError) {
    body = (
      <CmmsErrorState
        dense
        error={query.error}
        fallback={loadFailed}
        onRetry={query.refetch}
        isRetrying={query.isFetching}
      />
    );
  } else if (!rows?.length) {
    // An empty page of a list that is not empty: the orders on it were finished meanwhile, and
    // the tab is already on its way back to the last page.
    body = count > 0
      ? <CmmsListSkeleton rows={3} />
      : <CmmsEmptyState dense icon={emptyIcon} title={emptyTitle} hint={emptyHint} />;
  } else {
    body = (
      <>
        <Stack
          divider={<Divider sx={{ borderColor: cmmsColors.border }} />}
          sx={{ opacity: isPaging ? 0.5 : 1, transition: "opacity 150ms" }}
        >
          {rows.map((row) => (
            <React.Fragment key={getRowKey(row)}>{renderRow(row)}</React.Fragment>
          ))}
        </Stack>
        {footer}
      </>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ color: cmmsColors.text, fontWeight: 700, mb: 1 }}>
        {title}
        {/* The total, where the rows on screen may be only one page of it. */}
        {count > 0 && (
          <Typography component="span" variant="subtitle1" sx={{ color: cmmsColors.mutedText, fontWeight: 400, ml: 1 }}>
            {intl.formatNumber(count)}
          </Typography>
        )}
      </Typography>
      {body}
    </Box>
  );
}

export default function AssetMaintenanceTab({ companyKey, assetNodeId }) {
  const theme = useTheme();
  const intl = useIntl();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const enabled = Boolean(companyKey && assetNodeId);

  // 1-based, as the API counts.
  const [page, setPage] = useState(1);

  const workOrdersQuery = useQuery({
    // The work order page's prefix, so the CMMS SignalR events that already invalidate it refresh
    // this list when an order is raised or finished. `view` keeps the filter object apart from
    // the history tab's and from MachineWorkOrdersTab's plain { assetNodeId }.
    queryKey: ["cmmsWorkOrders", companyKey, { assetNodeId, view: "activePreventive", page, pageSize: WORK_ORDER_PAGE_SIZE }],
    queryFn: () => getAssetActivePreventiveWorkOrders(companyKey, assetNodeId, { page, pageSize: WORK_ORDER_PAGE_SIZE }),
    staleTime: 30_000,
    // The current page stays on screen while the next one loads, rather than the section
    // collapsing to a skeleton on every click of the pager.
    keepPreviousData: true,
    enabled,
  });

  // From the size the server echoes, which is the size it actually used.
  const workOrdersPage = workOrdersQuery.data;
  const pageCount = workOrdersPage ? Math.max(1, Math.ceil(workOrdersPage.totalCount / workOrdersPage.pageSize)) : 1;

  // Finishing the orders on the last page shrinks the total under it. Step back rather than
  // leave the section showing a page that no longer exists.
  useEffect(() => {
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount]);

  const schedulesQuery = useQuery({
    // Not AssetUsageTab's { assetNodeId }: that key holds the node's own active schedules from
    // the list endpoint, this one the subtree's with readings from the upcoming one. Sharing a
    // key would hand each tab the other's shape. A reset invalidates the whole prefix, so this
    // list moves with it.
    queryKey: ["cmmsRecurringSchedules", companyKey, { assetNodeId, view: "upcoming" }],
    queryFn: () => getUpcomingRecurringSchedules(companyKey, { assetNodeId }),
    staleTime: 60_000,
    enabled,
  });

  return (
    <Stack spacing={3}>
      <MaintenanceSection
        title={intl.formatMessage(messages.maintenanceWorkOrdersTitle)}
        count={workOrdersPage?.totalCount}
        query={workOrdersQuery}
        rows={workOrdersPage?.items}
        isPaging={workOrdersQuery.isPreviousData}
        footer={
          pageCount > 1 && (
            <Pagination
              size="small"
              count={pageCount}
              page={Math.min(page, pageCount)}
              onChange={(_, value) => setPage(value)}
              sx={{ display: "flex", justifyContent: "center", pt: 1 }}
            />
          )
        }
        loadFailed={intl.formatMessage(messages.maintenanceWorkOrdersLoadFailed)}
        emptyIcon={<AssignmentOutlinedIcon />}
        emptyTitle={intl.formatMessage(messages.maintenanceWorkOrdersEmptyTitle)}
        emptyHint={intl.formatMessage(messages.maintenanceWorkOrdersEmptyHint)}
        getRowKey={(workOrder) => workOrder.id}
        renderRow={(workOrder) => <WorkOrderRow workOrder={workOrder} cmmsColors={cmmsColors} />}
        cmmsColors={cmmsColors}
      />
      <MaintenanceSection
        title={intl.formatMessage(messages.maintenanceSchedulesTitle)}
        query={schedulesQuery}
        rows={schedulesQuery.data}
        loadFailed={intl.formatMessage(messages.maintenanceSchedulesLoadFailed)}
        emptyIcon={<EventNoteIcon />}
        emptyTitle={intl.formatMessage(messages.maintenanceSchedulesEmptyTitle)}
        emptyHint={intl.formatMessage(messages.maintenanceSchedulesEmptyHint)}
        getRowKey={(schedule) => schedule.scheduleId}
        renderRow={(schedule) => <UpcomingScheduleRow schedule={schedule} cmmsColors={cmmsColors} />}
        cmmsColors={cmmsColors}
      />
    </Stack>
  );
}
